/**
 * What this build points at.
 *
 * The API's origin is the only thing about the SPA that differs between a
 * laptop, staging and production, and it has to be decided at BUILD time: the
 * bundle is static files, so there is no server left to read an environment
 * variable at boot.
 *
 * Bun's bundler REPLACES `process.env.BUN_PUBLIC_*` with a string literal, and
 * only that prefix — nothing else about the shell reaches the browser. When the
 * variable is not set the identifier is left exactly as written, and `process`
 * does not exist in a browser, which is what the `typeof` guard is for. It is
 * not defensive coding; it is the other half of how the replacement works.
 *
 * The defaults are the local stack (PLAN.md §2). `bun run build:staging` and
 * `build:production` supply the real values; they deliberately have no defaults
 * of their own, because a deploy that quietly talked to localhost would look
 * fine in CI and be broken in front of everybody.
 */
/**
 * Reading one of those variables, safely, in both cases.
 *
 * The obvious guard — `typeof process === "undefined" ? undefined : process.env…`
 * — is WRONG here, and was: it is not part of the expression Bun replaces, so it
 * survives into the bundle and keeps asking a browser for a `process` that is
 * not there. The literal was inlined correctly and then never read, so every
 * build took the localhost fallback, `build:production` included. (Found while
 * running the e2e suite on non-default ports, where the SPA kept calling :3000.)
 *
 * A `try`/`catch` is the version that works: when the variable IS set the whole
 * member expression is already a string literal and nothing can throw; when it
 * is NOT set the identifier survives, the read throws `ReferenceError` in a
 * browser, and the fallback is the right answer anyway.
 */
export function publicEnv(read: () => string | undefined): string | undefined {
  try {
    return read();
  } catch {
    return undefined; // not set at build time — `process` is not a browser global
  }
}

const RAW_API_URL = publicEnv(() => process.env.BUN_PUBLIC_API_URL);
/**
 * "/" means SAME ORIGIN, and it is what every build is given: the dev proxy's
 * (apps/web/dev.ts), the e2e tier's, and every deployed bundle's (release.yml
 * refuses anything else), because the apex serves the app and the api both
 * (ruling R-0926-1 (1)). It normalises here to the empty base every fetch
 * concatenates with.
 *
 * The `=== ""` arm is the belt. An empty BUN_PUBLIC_* IS inlined, as `""`
 * (verified on Bun 1.4.0 — an earlier note here claimed the opposite), so
 * anyone who exports `BUN_PUBLIC_API_URL=` gets the same same-origin answer as
 * the sentinel rather than the localhost fallback. Only an UNSET variable
 * leaves the identifier unreplaced, and that is the case `publicEnv` catches.
 */
export const API_URL =
  RAW_API_URL === "/" || RAW_API_URL === "" ? "" : (RAW_API_URL ?? "http://localhost:3000");

/**
 * Where auth providers and Stripe send the browser back to. In a browser this
 * is `window.location.origin` — the SPA is wherever it was served from — so
 * this is only ever the fallback for the places there is no window: the test
 * runner and anything rendering to a string.
 */
export const WEB_ORIGIN =
  publicEnv(() => process.env.BUN_PUBLIC_WEB_URL) ?? "http://localhost:3001";

/**
 * The marketing site's About page (owner, 2026-09-25; wave 88). A build
 * parameter because each environment chooses its own; the fallback is for the
 * local stack and the test runner, and `release.yml` refuses to build a
 * deployed bundle without it. `||`, not `??`: an empty variable is inlined as
 * `""`, and an About link to "" would point at the page it is on.
 */
export const ABOUT_URL =
  publicEnv(() => process.env.BUN_PUBLIC_ABOUT_URL) || "https://nob.me/about";
